import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";

/**
 * The sales → ops kickoff calendar link.
 *
 * The live calendar ships as a backend default, but the URL is also an
 * admin-editable app setting, so ops can point the intake booking button at a
 * different calendar (or host) without a deploy. If the link is ever cleared,
 * `configured` is false and every surface degrades honestly instead of
 * rendering a dead button.
 */
export default function useOpsBriefingLink() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/intake/ops-briefing-link");
      setSettings(res.data);
    } catch (_) {
      // A missing link must never block the intake flow — treat a failed
      // fetch the same as "not configured yet".
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (payload) => {
    setSaving(true);
    try {
      const res = await apiClient.put("/intake/ops-briefing-link", payload);
      setSettings(res.data);
      toast.success(res.data?.configured ? "Booking link saved" : "Booking link cleared");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save the booking link");
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    settings,
    loading,
    saving,
    save,
    reload: load,
    bookingUrl: settings?.booking_url || "",
    configured: !!settings?.configured,
    canEdit: !!settings?.can_edit,
    calendarName: settings?.calendar_name || "Sales → Ops Kickoff",
    hostName: settings?.host_name || "",
  };
}
